import { logger, shortUuid } from '@tunasong/models'
import { features, useThunkDispatch } from '@tunasong/redux'

/**
 * Save a take's recording as an audio entity and return its id for `source.entityId`.
 *
 * The recording is kept, always (§2: notes are canonical, the source recording is never discarded).
 *
 * A failed upload does NOT fail the capture. The notes are what the user came for, and losing a
 * performance because storage was unreachable would be the wrong trade; the melody then carries
 * `source.kind = 'audio'` with no `entityId`, which is honest - there was a recording, and it is not
 * here.
 */
export interface CaptureSave {
  saveRecording: (blob: Blob, name: string, parentId?: string) => Promise<string | null>
}

export const useCaptureSave = (): CaptureSave => {
  const dispatch = useThunkDispatch()

  const saveRecording = async (blob: Blob, name: string, parentId?: string): Promise<string | null> => {
    const id = shortUuid()
    try {
      await dispatch(
        features.storage.thunks.uploadStoredEntity({
          id,
          name,
          type: 'audio',
          /** `null`, not `undefined`: a take from an embedded melody in a scratch document has no parent. */
          parentId: parentId ?? null,
          blob,
          message: 'Saving the take...',
        })
      ).unwrap()
      return id
    } catch (error) {
      logger.warn('melody capture: the take was not saved as an audio entity', error)
      return null
    }
  }

  return { saveRecording }
}
